import React from "react";
import strings from "../../core/translate";
import {
  getScopeGroups,
  extraActions,
  ACTION_READ,
  ACTION_WRITE,
} from "../../shared/scope-groups";

/**
 * One row per resource, one checkbox per action: what an account may view and
 * what it may change. `value` / `onChange` work on a flat array of scopes.
 */
export default class PermissionMatrix extends React.Component {
  constructor(props) {
    super(props);
    this.state = { search: "" };
    this.groups = getScopeGroups();
  }

  has(scope) {
    return scope ? (this.props.value || []).includes(scope) : false;
  }

  toggle(scope, checked) {
    if (!scope) return;
    const current = this.props.value || [];
    let next;

    if (checked) {
      next = current.includes(scope) ? current : [...current, scope];
      // being able to change something implies being able to see it
      const group = this.groups.find((it) => it.actions[ACTION_WRITE] === scope);
      const read = group && group.actions[ACTION_READ];
      if (read && !next.includes(read)) {
        next = [...next, read];
      }
    } else {
      next = current.filter((it) => it !== scope);
      // dropping view access drops edit access with it
      const group = this.groups.find((it) => it.actions[ACTION_READ] === scope);
      const write = group && group.actions[ACTION_WRITE];
      if (write) {
        next = next.filter((it) => it !== write);
      }
    }

    this.props.onChange(next);
  }

  toggleRow(group, checked) {
    const scopes = Object.values(group.actions);
    const current = this.props.value || [];
    this.props.onChange(
      checked
        ? Array.from(new Set([...current, ...scopes]))
        : current.filter((scope) => !scopes.includes(scope))
    );
  }

  toggleAll(checked) {
    if (checked) {
      const all = this.groups.reduce(
        (acc, group) => acc.concat(Object.values(group.actions)),
        []
      );
      this.props.onChange(Array.from(new Set(all)));
    } else {
      this.props.onChange([]);
    }
  }

  visibleGroups() {
    const term = this.state.search.trim().toLowerCase();
    if (!term) return this.groups;
    return this.groups.filter(
      (group) =>
        group.label.toLowerCase().includes(term) ||
        group.resource.toLowerCase().includes(term)
    );
  }

  render() {
    const { value = [], disabled = false, error } = this.props;
    const groups = this.visibleGroups();
    const total = this.groups.reduce(
      (count, group) => count + Object.keys(group.actions).length,
      0
    );

    return (
      <div className={`permissions${disabled ? " permissions--disabled" : ""}`}>
        <div className="permissions__toolbar">
          <div className="permissions__search">
            <i className="nc-icon nc-zoom-split" />
            <input
              type="text"
              value={this.state.search}
              placeholder={strings.search_permissions}
              onChange={(e) => this.setState({ search: e.target.value })}
            />
          </div>
          <span className="permissions__count">
            {value.length}/{total}
          </span>
          <button
            type="button"
            className="permissions__bulk"
            disabled={disabled}
            onClick={() => this.toggleAll(true)}
          >
            {strings.selectall}
          </button>
          <button
            type="button"
            className="permissions__bulk"
            disabled={disabled}
            onClick={() => this.toggleAll(false)}
          >
            {strings.clear_all}
          </button>
        </div>

        <div className="permissions__table">
          <div className="permissions__head">
            <span>{strings.section}</span>
            <span>{strings.can_view}</span>
            <span>{strings.can_edit}</span>
          </div>

          {groups.map((group) => {
            const read = group.actions[ACTION_READ];
            const write = group.actions[ACTION_WRITE];
            const extras = extraActions(group);
            const rowChecked = Object.values(group.actions).every((scope) =>
              this.has(scope)
            );

            return (
              <div className="permissions__row" key={group.resource}>
                <div className="permissions__name">
                  <button
                    type="button"
                    className="permissions__row-toggle"
                    disabled={disabled}
                    onClick={() => this.toggleRow(group, !rowChecked)}
                    title={strings.selectall}
                  >
                    {group.label}
                  </button>
                  <span className="permissions__resource">
                    {group.resource}
                  </span>
                  {extras.length > 0 && (
                    <span className="permissions__extras">
                      {extras.map((action) => (
                        <label key={action} className="permissions__extra">
                          <input
                            type="checkbox"
                            disabled={disabled}
                            checked={this.has(group.actions[action])}
                            onChange={(e) =>
                              this.toggle(group.actions[action], e.target.checked)
                            }
                          />
                          {action}
                        </label>
                      ))}
                    </span>
                  )}
                </div>

                <div className="permissions__cell">
                  {read ? (
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={this.has(read)}
                      onChange={(e) => this.toggle(read, e.target.checked)}
                    />
                  ) : (
                    <span className="permissions__na">—</span>
                  )}
                </div>

                <div className="permissions__cell">
                  {write ? (
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={this.has(write)}
                      onChange={(e) => this.toggle(write, e.target.checked)}
                    />
                  ) : (
                    <span className="permissions__na">—</span>
                  )}
                </div>
              </div>
            );
          })}

          {groups.length === 0 && (
            <div className="permissions__empty">{strings.nodata}</div>
          )}
        </div>

        {error && <span className="mfield__error">{error}</span>}
      </div>
    );
  }
}
